import type { GpoRequestStatus, InquirySource, QuotePriceUnit, RestaurantInquiryItem } from 'apis/restaurant-inquiries';
import { RestaurantInquiryItemStatus } from 'apis/restaurant-inquiries';
import type { UomLadder } from 'utils/price-units';
import { availableTiers, convertPrice } from 'utils/price-units';

export const isAppInquiry = (inquiry?: { source?: InquirySource } | null) => inquiry?.source === 'APP';
// gpoOrderId is the usual signal, but a freshly-created app item can carry the
// live gpoRequest before gpoOrderId is backfilled on the row — either is proof.
export const isAppItem = (item?: { gpoOrderId?: number | null; gpoRequest?: unknown | null } | null) =>
  item?.gpoOrderId != null || item?.gpoRequest != null;

// Labels are the ones the restaurant sees in the app, so ops and the customer use the same words.
export const APP_REQUEST_STATE_META: Record<GpoRequestStatus, { label: string; fg: string; bg: string }> = {
  pending: { label: 'Awaiting price', fg: '#b45309', bg: '#fef3c7' },
  confirmed: { label: 'Price confirmed', fg: '#047857', bg: '#d1fae5' },
  placed: { label: 'Ordered', fg: '#1d4ed8', bg: '#dbeafe' },
  cancelled: { label: 'Cancelled', fg: '#6b7280', bg: '#eef1f6' },
  expired: { label: 'Expired', fg: '#b91c1c', bg: '#fee2e2' },
};

const MAX_CONFIRMED_UNIT_PRICE = 9_999_999.99;
const HOUR_IN_MS = 60 * 60 * 1000;

// Render-safe money formatting for gpoRequest/awardPreview values, which can
// legitimately be null (no preview yet) or arrive malformed from the API —
// never let a bad value throw mid-render. Number(null) is 0 (finite), so null
// is excluded explicitly rather than relying on Number.isFinite alone.
export const formatMoney2 = (n: unknown): string =>
  n != null && Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '—';

/**
 * What the restaurant will be shown if this quote is awarded: the quote price converted to the
 * master's BASE unit. Mirrors UMS `quoteToBaseUnitPrice` rule for rule — the stored rung when the
 * ladder still defines it, else the OUTERMOST defined rung (NOT resolveTier's defaultUnit
 * fallback: the BE does not apply it, and a preview that disagrees with the push is worse than none).
 * Null = the BE will refuse the award (422).
 */
export function previewBaseUnitPrice(
  quote: { price: number | string; priceUnit?: QuotePriceUnit | null },
  ladder: UomLadder
): number | null {
  const tiers = availableTiers(ladder);
  if (!tiers.length) return null;
  const tier =
    quote.priceUnit && tiers.some((entry) => entry.tier === quote.priceUnit) ? quote.priceUnit : tiers[0].tier;

  const converted = convertPrice(Number(quote.price), tier, 'BASE', ladder);
  if (converted == null || !Number.isFinite(converted)) return null;

  const rounded = Math.round((converted + Number.EPSILON) * 100) / 100;
  return rounded > 0 && rounded <= MAX_CONFIRMED_UNIT_PRICE ? rounded : null;
}

export function describeExpiry(expiresAt: string | null, now: number): { text: string; urgent: boolean } | null {
  if (!expiresAt) return null;
  const remaining = new Date(expiresAt).getTime() - now;
  // null stays reserved for "no expiry to show" (absent expiresAt) — once the
  // window has actually lapsed the pill should say so, not fall silent.
  if (remaining <= 0) return { text: 'expired', urgent: true };
  return remaining < HOUR_IN_MS
    ? { text: 'expires soon', urgent: true }
    : { text: `valid for ${Math.floor(remaining / HOUR_IN_MS)}h`, urgent: false };
}

export const canDeclineAppItem = (item: RestaurantInquiryItem) =>
  isAppItem(item) &&
  item.status !== RestaurantInquiryItemStatus.FULFILLED &&
  item.status !== RestaurantInquiryItemStatus.UNFULFILLED;

/**
 * Null = awardable as far as the FE can tell. The BE re-checks under a row lock.
 * quoteMasterId is the offered quote's product's master id (via its
 * masterProduct link) — the BE 422s (QUOTE_PRODUCT_MISMATCH) when it differs
 * from the item's own masterProductId, so block it here first with a
 * readable reason instead of surfacing the raw 422.
 */
export function awardBlockReason(
  item: RestaurantInquiryItem,
  preview: number | null,
  quoteMasterId?: number | null
): string | null {
  if (!isAppItem(item)) return null;
  const state = item.gpoRequest?.status;
  if (state === 'cancelled') return 'The restaurant cancelled this request in the app.';
  if (state === 'placed') return 'The restaurant already placed this order in the app.';
  if (state === 'confirmed') return 'This request already has a confirmed price — revert that award first.';
  if (quoteMasterId != null && item.masterProductId != null && quoteMasterId !== item.masterProductId) {
    return 'This quote is for a different product than the one the restaurant requested in the app.';
  }
  if (preview == null) {
    return 'This quote cannot be converted to a price per base unit. Set its price unit (and the pack size / net content on the product) first.';
  }
  return null;
}
